import {
  ArrowDownTrayIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import {
  ActionIcon,
  Button,
  Center,
  Group,
  Modal,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useEffect, useState } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';

import {
  deleteLesson,
  getBookAllLessons,
} from '@/entities/books/api/book-lessons-api.ts';
import { BookLessons } from '@/entities/books/model/types.ts';
import { useProjectPath } from '@/shared/projects/project-path';

import styles from './book-lessons-list.module.scss';

export const BookLessonsList = () => {
  const [lessonsList, setLessonsList] = useState<BookLessons[] | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [lessonToDelete, setLessonToDelete] = useState<number | null>(null);
  const navigate = useNavigate();
  const withProject = useProjectPath();
  const { bookId } = useParams();

  // const {
  //   finalStatusReached,
  //   statusText,
  //   data: uploadStatusResponse,
  // } = useAudioUploadStatus();
  // const uploadStatus = uploadStatusResponse;

  useEffect(() => {
    if (!bookId) return;

    const fetchLessons = async () => {
      if (bookId) {
        try {
          const { lessons } = await getBookAllLessons(bookId);
          setLessonsList(lessons);
        } catch (error) {
          console.error('Ошибка при загрузке списка уроков:', error);
        }
      }
    };

    fetchLessons();
  }, [bookId]);

  useEffect(() => {
    if (!bookId) return;

    // if (
    //   finalStatusReached &&
    //   statusText === AudioProcessingStatus.SPLITTED &&
    //   bookId
    // ) {
    getBookAllLessons(bookId)
      .then(({ lessons }) => setLessonsList(lessons))
      .catch((error) => console.error(error));
    // }
  }, [bookId]);

  const handleAddLessonClick = () => {
    navigate(withProject(`/books/${bookId}/lesson/new`));
  };

  const handleDelete = async () => {
    if (lessonToDelete !== null) {
      try {
        await deleteLesson(lessonToDelete);
        if (lessonsList) {
          const updatedBooks = lessonsList.filter(
            (lesson) => lesson.id !== lessonToDelete,
          );
          setLessonsList(updatedBooks);
          setIsModalOpen(false);
          setLessonToDelete(null);
        }
      } catch (error) {
        console.error('Ошибка при удалении урока:', error);
      }
    }
  };

  const openDeleteModal = (id: number | undefined) => {
    if (id) {
      setLessonToDelete(id);
      setIsModalOpen(true);
    }
  };

  const closeDeleteModal = () => {
    setIsModalOpen(false);
    setLessonToDelete(null);
  };

  const handleBatchUploadClick = () => {
    navigate(withProject(`/books/${bookId}/lessons/batch-upload`));
  };

  const handleDownloadAudio = (audioUrl: string, lessonTitle: string) => {
    if (audioUrl) {
      const sanitizedTitle = lessonTitle.replace(/[<>:"/\\|?*]/g, '').trim();
      const filename = sanitizedTitle ? `${sanitizedTitle}.mp3` : 'audio.mp3';

      // Create hidden anchor and trigger download
      const link = document.createElement('a');
      link.href = audioUrl;
      link.download = filename;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Show user a message about the filename since browser might ignore it
      console.log(`Downloading: ${filename}`);
    }
  };

  // const renderUploadingStatus = () => {
  //   let statusMessage = 'Файлы загружаются...';
  //
  //   // if (statusText === AudioProcessingStatus.ERROR) {
  //   //   statusMessage = 'Ошибка при обработке аудио';
  //   // } else if (uploadStatus?.message) {
  //   //   statusMessage = uploadStatus.message;
  //   // }
  //
  //   // const isError = statusText === AudioProcessingStatus.ERROR;
  //
  //   return (
  //     <Center h={200}>
  //       <Stack align="center">
  //         {!isError && <Loader size="md" />}
  //         <Text color={isError ? 'red' : undefined}>{statusMessage}</Text>
  //       </Stack>
  //     </Center>
  //   );
  // };

  return (
    <div className={styles.lessonsList}>
      <Group className={styles.lessonsList__header} justify="space-between">
        <Title order={3}>Список уроков</Title>
        <Group>
          <Button variant="default" onClick={handleBatchUploadClick}>
            Массовая загрузка
          </Button>
          {lessonsList ? (
            <Button onClick={handleAddLessonClick}>+ Добавить урок</Button>
          ) : null}
        </Group>
      </Group>

      <div>
        {/*{statusText === AudioProcessingStatus.WAITING &&*/}
        {/*  renderUploadingStatus()}*/}

        {
          <>
            {lessonsList && lessonsList.length > 0 ? (
              lessonsList.map((lesson) => (
                <div
                  key={lesson.id || lesson.bookId}
                  className={styles.lessonsList__item}
                >
                  <span className={styles.lessonsList__number}>
                    {lesson.index}
                  </span>
                  <div className={styles.lessonsList__title}>
                    {lesson.title}
                  </div>
                  <div className={styles.lessonsList__audio}>
                    {/* Audio books do not have a separate captions track. */}
                    {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                    <audio controls src={lesson.audioUrl ?? ''}>
                      Ваш браузер не поддерживает аудио элемент.
                    </audio>
                  </div>
                  <Group className={styles.actionButtons}>
                    <NavLink
                      to={withProject(`/books/${bookId}/lesson/${lesson.id}`)}
                    >
                      <ActionIcon className={styles.button}>
                        <PencilSquareIcon width={20} height={20} />
                      </ActionIcon>
                    </NavLink>
                    <ActionIcon
                      className={styles.button}
                      onClick={() =>
                        handleDownloadAudio(lesson.audioUrl ?? '', lesson.title)
                      }
                      title="Скачать аудио"
                    >
                      <ArrowDownTrayIcon width={20} height={20} />
                    </ActionIcon>
                    <div>
                      <ActionIcon
                        className={styles.button}
                        onClick={() => openDeleteModal(lesson.id)}
                      >
                        <TrashIcon width={20} height={20} />
                      </ActionIcon>
                    </div>
                  </Group>
                  <Modal
                    opened={isModalOpen}
                    onClose={closeDeleteModal}
                    title="Подтвердите удаление"
                  >
                    <Text>Вы уверены, что хотите удалить этот урок?</Text>
                    <div className={styles.deleteBtn}>
                      <Button
                        color="red"
                        onClick={handleDelete}
                        className={styles.redBtn}
                      >
                        Удалить
                      </Button>
                      <Button onClick={closeDeleteModal}>Отмена</Button>
                    </div>
                  </Modal>
                </div>
              ))
            ) : (
              <Center style={{ height: '200px' }}>
                <Stack align="center">
                  <div>Нет добавленных аудиофайлов</div>
                  <Button onClick={handleAddLessonClick}>
                    + Добавить урок
                  </Button>
                </Stack>
              </Center>
            )}
          </>
        }
      </div>
    </div>
  );
};
